import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { type ReactNode, useState } from 'react';
import { Button } from '@/shared/ui';

interface AnnouncementBannerProps {
  storageKey: string;
  bannerText: string;
  dialogTitle: string;
  dialogContent: ReactNode;
  confirmLabel?: string;
}

export function AnnouncementBanner({
  storageKey,
  bannerText,
  dialogTitle,
  dialogContent,
  confirmLabel = 'Understood',
}: AnnouncementBannerProps) {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(storageKey) === 'true',
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const dismiss = () => {
    localStorage.setItem(storageKey, 'true');
    setDismissed(true);
    setDialogOpen(false);
  };

  if (dismissed) return null;

  return (
    <>
      <Alert
        severity="info"
        sx={{
          borderRadius: 0,
          flexShrink: 0,
          '& .MuiAlert-action': { alignItems: 'center', pt: 0 },
        }}
        action={
          <IconButton
            size="small"
            onClick={dismiss}
            aria-label="Dismiss banner"
            sx={{ color: 'inherit' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        {bannerText}{' '}
        <Button
          size="small"
          disableRipple
          sx={{
            color: 'inherit',
            textTransform: 'none',
            textDecoration: 'underline',
            minWidth: 0,
            p: 0,
            verticalAlign: 'baseline',
            lineHeight: 'inherit',
            fontSize: 'inherit',
            backgroundColor: 'transparent',
            '&:hover': {
              color: 'inherit',
              textDecoration: 'underline',
              backgroundColor: 'transparent',
            },
          }}
          onClick={() => setDialogOpen(true)}
        >
          Learn more
        </Button>
      </Alert>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: 1 }}
        >
          <span>{dialogTitle}</span>
          <IconButton
            size="small"
            onClick={() => setDialogOpen(false)}
            sx={{ ml: 'auto', flexShrink: 0, mt: '-2px' }}
            aria-label="Close dialog"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{dialogContent}</DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={dismiss}>
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
